import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { eur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import { AvisoTablas } from "@/components/registry/AddressFields";
import { useRegistryRefdata } from "@/components/registry/useRegistryRefdata";
import { toast } from "sonner";

const EMPTY = {
  name: "", slug: "", plate: "", make: "", model: "", year: "", vin: "", color: "", registry_make: "", registry_color: "",
  first_registration: "", kind: "camper", seats: 4, beds: 2,
  description: { es: "", ca: "", en: "", fr: "" }, photos: [],
  base_price_per_night: 0, min_nights: 1, deposit_amount: 900,
  km_included_per_day: 100, extra_km_rate: 0.2,
  insurer: "", policy_number: "", traccar_device_id: "", key_hint: "",
  base_lat: "", base_lng: "", active: true, sort_order: 100,
  equipment: null, // null = la lista por defecto del servidor
};
const KINDS = { camper: "Camper", autocaravana: "Autocaravana", furgoneta: "Furgoneta", otro: "Otro" };
const LANGS = [["es", "Castellano"], ["ca", "Català"], ["en", "English"], ["fr", "Français"]];
const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";
const selectCls = "mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm";

// Opciones de una lista oficial del registro de viajeros (marcas o colores de los Mossos). Un valor guardado que no
// está en la lista se sigue viendo, marcado, en vez de dejar el selector en blanco; mientras la lista no ha llegado
// no se sabe si está, así que va sin marca.
function OpcionesOficiales({ lista, valor }) {
  const cargada = Array.isArray(lista);
  const fuera = Boolean(valor) && !(cargada && lista.includes(valor));
  return (
    <>
      <option value="">—</option>
      {fuera && <option value={valor}>{cargada ? `${valor} (no está en la lista oficial)` : valor}</option>}
      {(lista || []).map((v) => <option key={v} value={v}>{v}</option>)}
    </>
  );
}

export function slugify(text) {
  return (text || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}

const numOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Clave estable de un elemento de equipamiento: se guarda en las entregas y
// devoluciones, así que no cambia al renombrarlo.
function equipmentKey(label, taken) {
  const base = slugify(label).replace(/-/g, "_").slice(0, 36) || "elemento";
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}_${n}`;
  return key;
}

function withKeys(items) {
  const taken = new Set(items.filter((e) => e.key).map((e) => e.key));
  return items
    .filter((e) => (e.label || "").trim())
    .map((e) => {
      if (e.key) return { key: e.key, label: e.label.trim(), group: (e.group || "Otros").trim() || "Otros" };
      const key = equipmentKey(e.label, taken);
      taken.add(key);
      return { key, label: e.label.trim(), group: (e.group || "Otros").trim() || "Otros" };
    });
}

function EquipmentEditor({ value, onChange }) {
  if (!value) {
    return (
      <p className="text-sm text-[#5C5C5C]">
        Se usará la lista de equipamiento por defecto. Podrás ajustarla después de guardar el vehículo.
      </p>
    );
  }
  const set = (i, k) => (e) => onChange(value.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));
  return (
    <div className="space-y-2" data-testid="fleet-equipment">
      {value.map((it, i) => (
        <div key={it.key || `nuevo-${i}`} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
          <Input className="bg-[#F7F5F0] border-[#DCD8D0]" placeholder="Grupo" value={it.group} onChange={set(i, "group")} />
          <Input className="bg-[#F7F5F0] border-[#DCD8D0]" placeholder="Elemento" value={it.label} onChange={set(i, "label")}
            data-testid={`fleet-eq-label-${i}`} />
          <Button variant="ghost" size="sm" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Quitar">✕</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...value, { key: "", label: "", group: value[value.length - 1]?.group || "" }])}
        data-testid="fleet-eq-add">
        Añadir elemento
      </Button>
    </div>
  );
}

function toPayload(d) {
  const description = Object.fromEntries(
    Object.entries(d.description || {}).filter(([, t]) => (t || "").trim()),
  );
  const payload = {
    ...d,
    year: numOrNull(d.year),
    seats: Number(d.seats), beds: Number(d.beds),
    base_price_per_night: Number(d.base_price_per_night), min_nights: Number(d.min_nights),
    deposit_amount: Number(d.deposit_amount), km_included_per_day: Number(d.km_included_per_day),
    extra_km_rate: Number(d.extra_km_rate), sort_order: Number(d.sort_order),
    base_lat: numOrNull(d.base_lat), base_lng: numOrNull(d.base_lng),
    description,
    equipment: d.equipment ? withKeys(d.equipment) : undefined,
  };
  if (!payload.equipment) delete payload.equipment;
  delete payload.id; delete payload.created_at; delete payload.updated_at;
  return payload;
}

export default function Fleet() {
  const refdata = useRegistryRefdata(true);
  const uid = useId();
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/fleet/vehicles");
      setVehicles(data.vehicles || []);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bind = (k) => ({
    value: editing?.[k] ?? "",
    onChange: (e) => setEditing((d) => ({ ...d, [k]: e.target.value })),
  });
  const setName = (e) => {
    const name = e.target.value;
    setEditing((d) => ({ ...d, name, slug: d.id ? d.slug : slugify(name) }));
  };
  const setDesc = (lang) => (e) =>
    setEditing((d) => ({ ...d, description: { ...(d.description || {}), [lang]: e.target.value } }));

  const openEdit = (v) =>
    setEditing({ ...EMPTY, ...v, year: v.year ?? "", base_lat: v.base_lat ?? "", base_lng: v.base_lng ?? "",
                 description: { ...EMPTY.description, ...(v.description || {}) } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = toPayload(editing);
      if (editing.id) await api.put(`/fleet/vehicles/${editing.id}`, payload);
      else await api.post("/fleet/vehicles", payload);
      toast.success("Vehículo guardado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyIcal = async (v) => {
    try {
      const { data } = await api.get(`/fleet/vehicles/${v.id}/ical`);
      await navigator.clipboard.writeText(data.url);
      toast.success("Enlace iCal copiado");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const remove = async (v) => {
    try {
      await api.delete(`/fleet/vehicles/${v.id}`);
      toast.success("Vehículo borrado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setConfirmId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <Button onClick={() => setEditing({ ...EMPTY })} className="bg-acento hover:bg-acento-hover text-white" data-testid="fleet-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo vehículo
          </Button>
        </div>
      </header>

      <main className="container-x py-8">
        <span className="text-overline">Flota</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] mt-2 mb-6">Vehículos</h1>

        {vehicles.length === 0 ? (
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#5C5C5C]">
            Aún no hay vehículos. Crea el primero para poder hacer reservas.
          </div>
        ) : (
          <div className="bg-white border border-[#DCD8D0] rounded-xl divide-y divide-[#EAE7E0]">
            {vehicles.map((v) => (
              <div key={v.id} className="flex items-center gap-4 p-4" data-testid={`fleet-row-${v.slug}`}>
                {v.photos?.[0] ? (
                  <img src={v.photos[0]} alt="" className="h-14 w-20 rounded-lg object-cover border border-[#DCD8D0]" />
                ) : (
                  <div className="h-14 w-20 rounded-lg bg-[#EAE7E0]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1A1A1A] flex items-center gap-2">
                    {v.name}
                    {!v.active && <span className="text-xs bg-[#EAE7E0] text-[#5C5C5C] px-2 py-0.5 rounded">Desactivado</span>}
                  </div>
                  <div className="text-xs text-[#8A8A8A]">
                    <span className="font-mono">{v.plate}</span> · {KINDS[v.kind] || v.kind} · {eur(v.base_price_per_night)}/noche · fianza {v.deposit_amount} €
                  </div>
                </div>
                <Button variant="ghost" onClick={() => copyIcal(v)} data-testid={`fleet-ical-${v.slug}`}>
                  iCal
                </Button>
                <Button variant="ghost" onClick={() => openEdit(v)} data-testid={`fleet-edit-${v.slug}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {confirmId === v.id ? (
                  <Button variant="destructive" onClick={() => remove(v)} data-testid={`fleet-delete-confirm-${v.slug}`}>
                    ¿Borrar?
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={() => setConfirmId(v.id)} data-testid={`fleet-delete-${v.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar vehículo" : "Nuevo vehículo"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-6" data-testid="fleet-form">
              <section className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Nombre público</Label><Input className={inputCls} value={editing.name} onChange={setName} data-testid="fleet-name" /></div>
                <div><Label>Matrícula</Label><Input className={inputCls} {...bind("plate")} data-testid="fleet-plate" /></div>
                <div><Label>Identificador (web)</Label><Input className={inputCls} {...bind("slug")} data-testid="fleet-slug" /></div>
                <div><Label>Marca</Label><Input className={inputCls} {...bind("make")} /></div>
                <div><Label>Modelo</Label><Input className={inputCls} {...bind("model")} /></div>
                <div><Label>Año</Label><Input type="number" className={inputCls} {...bind("year")} /></div>
                <div><Label>1ª matriculación</Label><Input className={inputCls} placeholder="dd/mm/aaaa" {...bind("first_registration")} /></div>
                <div><Label>Color</Label><Input className={inputCls} {...bind("color")} /></div>
                <div><Label htmlFor={`${uid}-vin`}>Bastidor (VIN, hasta 17 letras y cifras)</Label><Input id={`${uid}-vin`} className={inputCls} {...bind("vin")} data-testid="fleet-vin" /></div>
                <div>
                  <Label htmlFor={`${uid}-registry-make`}>Marca del chasis (registro de viajeros)</Label>
                  <select id={`${uid}-registry-make`} className={selectCls} {...bind("registry_make")} data-testid="fleet-registry-make">
                    <OpcionesOficiales lista={refdata?.vehicle_brands} valor={editing.registry_make} />
                  </select>
                </div>
                <div>
                  <Label htmlFor={`${uid}-registry-color`}>Color (registro de viajeros)</Label>
                  <select id={`${uid}-registry-color`} className={selectCls} {...bind("registry_color")} data-testid="fleet-registry-color">
                    <OpcionesOficiales lista={refdata?.vehicle_colors} valor={editing.registry_color} />
                  </select>
                </div>
                {refdata?.failed && <div className="sm:col-span-3"><AvisoTablas refdata={refdata} listas="las listas de marcas y colores de los Mossos" /></div>}
                <div>
                  <Label>Tipo</Label>
                  <select className={selectCls} {...bind("kind")}>
                    {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div><Label>Plazas</Label><Input type="number" className={inputCls} {...bind("seats")} /></div>
                <div><Label>Camas</Label><Input type="number" className={inputCls} {...bind("beds")} /></div>
              </section>

              <section className="grid sm:grid-cols-3 gap-3">
                <div><Label>Precio base (€/noche)</Label><Input type="number" step="0.01" className={inputCls} {...bind("base_price_per_night")} data-testid="fleet-price" /></div>
                <div><Label>Mínimo de noches</Label><Input type="number" className={inputCls} {...bind("min_nights")} /></div>
                <div><Label>Fianza (€)</Label><Input type="number" className={inputCls} {...bind("deposit_amount")} /></div>
                <div><Label>Km incluidos por día</Label><Input type="number" className={inputCls} {...bind("km_included_per_day")} /></div>
                <div><Label>€ por km extra</Label><Input type="number" step="0.01" className={inputCls} {...bind("extra_km_rate")} /></div>
                <div><Label>Orden en listados</Label><Input type="number" className={inputCls} {...bind("sort_order")} /></div>
              </section>

              <section className="grid sm:grid-cols-2 gap-3">
                <div><Label>Aseguradora</Label><Input className={inputCls} {...bind("insurer")} /></div>
                <div><Label>N.º de póliza</Label><Input className={inputCls} {...bind("policy_number")} /></div>
              </section>

              <section className="grid sm:grid-cols-3 gap-3">
                <div><Label>Id del GPS en Traccar</Label><Input className={inputCls} {...bind("traccar_device_id")} /></div>
                <div><Label>Base: latitud</Label><Input className={inputCls} {...bind("base_lat")} /></div>
                <div><Label>Base: longitud</Label><Input className={inputCls} {...bind("base_lng")} /></div>
                <div className="sm:col-span-3"><Label>Dónde está la llave (recogida autónoma)</Label><Textarea className={inputCls} rows={2} {...bind("key_hint")} /></div>
              </section>

              <section className="space-y-2">
                <Label>Equipamiento (lo que se revisa en la entrega y en la devolución)</Label>
                <EquipmentEditor value={editing.equipment} onChange={(equipment) => setEditing((d) => ({ ...d, equipment }))} />
              </section>

              <section>
                <PhotoUploader label="Fotos" multiple adminUpload value={editing.photos || []}
                  onChange={(photos) => setEditing((d) => ({ ...d, photos }))} testid="fleet-photos" />
              </section>

              <section className="grid sm:grid-cols-2 gap-3">
                {LANGS.map(([lang, label]) => (
                  <div key={lang}><Label>Descripción · {label}</Label>
                    <Textarea className={inputCls} rows={3} value={editing.description?.[lang] || ""} onChange={setDesc(lang)} />
                  </div>
                ))}
              </section>

              <label className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                <Checkbox checked={!!editing.active} onCheckedChange={(v) => setEditing((d) => ({ ...d, active: Boolean(v) }))} />
                Activo (admite reservas nuevas)
              </label>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={save} disabled={saving} className="bg-marca hover:bg-marca-hover text-white" data-testid="fleet-save">
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
